// =============================================================================
// SHANIZ GAMING ADMIN - ANALYTICS / REPORTING DASHBOARD
// =============================================================================

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { statsService, RevenuePoint, UserGrowthPoint, GamePerformance } from '../services/api';
import { PermissionButton } from '../components/PermissionButton';
import { CHART_SERIES, seriesColor, chartTokens, tooltipProps } from '../theme/chartTheme';

function toCsv(rows: Array<Record<string, unknown>>): string {
  if (!rows.length) return '';
  const headers = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(
      headers
        .map((h) => {
          const v = r[h];
          if (v === null || v === undefined) return '';
          const s = String(v).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        })
        .join(',')
    );
  }
  return lines.join('\n');
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const Analytics: React.FC = () => {
  const theme = useTheme();
  const ct = chartTokens(theme);
  const tt = tooltipProps(theme);
  const [days, setDays] = useState(30);
  const [from, setFrom] = useState(() => dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedName, setSchedName] = useState('Weekly revenue');
  const [schedEmail, setSchedEmail] = useState('');
  const [schedCron, setSchedCron] = useState('0 8 * * 1');
  const [schedKind, setSchedKind] = useState<'revenue' | 'user_growth' | 'game_performance' | 'retention'>('revenue');
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  const overview = useQuery({ queryKey: ['stats', 'overview'], queryFn: statsService.overview });
  const revenue = useQuery({ queryKey: ['stats', 'revenue', days], queryFn: () => statsService.revenueSeries(days) });
  const growth = useQuery({ queryKey: ['stats', 'growth', days], queryFn: () => statsService.userGrowth(days) });
  const perf = useQuery({ queryKey: ['stats', 'games'], queryFn: statsService.gamePerformance });
  const cohorts = useQuery({ queryKey: ['stats', 'retention'], queryFn: statsService.retentionCohorts });

  const schedule = useMutation({
    mutationFn: statsService.scheduleReport,
    onSuccess: () => {
      setSchedOpen(false);
      setSnackbar({ open: true, msg: 'Report scheduled', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Schedule failed', sev: 'error' }),
  });

  const exportCsv = () => {
    const rows = [
      ...(revenue.data || []).map((r) => ({ section: 'revenue', ...r })),
      ...(growth.data || []).map((r) => ({ section: 'user_growth', ...r })),
      ...(perf.data || []).map((r) => ({ section: 'game_performance', ...r })),
      ...(cohorts.data || []).map((r) => ({ section: 'retention', ...r })),
    ];
    downloadBlob(`shaniz-analytics-${dayjs().format('YYYYMMDD')}.csv`, new Blob([toCsv(rows)], { type: 'text/csv' }));
  };

  const exportXlsx = async () => {
    try {
      type XlsxMod = { utils: { book_new: () => unknown; book_append_sheet: (wb: unknown, ws: unknown, n: string) => void; json_to_sheet: (data: unknown[]) => unknown }; writeFile: (wb: unknown, name: string) => void };
      // Dynamic import avoids loading xlsx on the main bundle.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mod = (await import('xlsx')) as unknown as XlsxMod;
      const wb = mod.utils.book_new();
      if (revenue.data) mod.utils.book_append_sheet(wb, mod.utils.json_to_sheet(revenue.data), 'Revenue');
      if (growth.data) mod.utils.book_append_sheet(wb, mod.utils.json_to_sheet(growth.data), 'UserGrowth');
      if (perf.data) mod.utils.book_append_sheet(wb, mod.utils.json_to_sheet(perf.data), 'GamePerformance');
      if (cohorts.data) mod.utils.book_append_sheet(wb, mod.utils.json_to_sheet(cohorts.data), 'Retention');
      mod.writeFile(wb, `shaniz-analytics-${dayjs().format('YYYYMMDD')}.xlsx`);
    } catch (e) {
      const err = e as Error;
      setSnackbar({ open: true, msg: err.message || 'XLSX export requires the "xlsx" package', sev: 'error' });
    }
  };

  const filteredRevenue = useMemo<RevenuePoint[]>(() => {
    const data = Array.isArray(revenue.data) ? revenue.data : [];
    return data
      .filter((r: any) => (from ? r?.date >= from : true) && (to ? r?.date <= to : true))
      .map((r: any) => ({
        date: r?.date ?? '',
        revenue: Number(r?.revenue) || 0,
        transactions: Number(r?.transactions) || 0,
      }));
  }, [revenue.data, from, to]);

  const filteredGrowth = useMemo<UserGrowthPoint[]>(() => {
    const data = Array.isArray(growth.data) ? growth.data : [];
    // Backend returns { date, newUsers, cumulative }; chart was hard-coded to read activeUsers.
    // Coerce both keys so the two Area series render even when the backend changes the key.
    return data
      .filter((r: any) => (from ? r?.date >= from : true) && (to ? r?.date <= to : true))
      .map((r: any) => ({
        date: r?.date ?? '',
        newUsers: Number(r?.newUsers) || 0,
        activeUsers: Number(r?.activeUsers ?? r?.cumulative ?? 0) || 0,
      }));
  }, [growth.data, from, to]);

  const cohortGrid = useMemo(() => {
    const data = (Array.isArray(cohorts.data) ? cohorts.data : []).filter((c: any) => c && typeof c === 'object');
    const cohortList = Array.from(new Set(data.map((c: any) => String(c.cohort ?? '')))).sort();
    const periods = Array.from(new Set(data.map((c: any) => Number(c.period) || 0))).sort((a, b) => a - b);
    const map = new Map<string, number>();
    for (const c of data) map.set(`${c.cohort}:${c.period}`, Number((c as any).retention) || 0);
    return { cohortList, periods, map };
  }, [cohorts.data]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" fontWeight={700}>Analytics & Reports</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => { overview.refetch(); revenue.refetch(); growth.refetch(); perf.refetch(); cohorts.refetch(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <PermissionButton permission="reports.export" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv}>CSV</PermissionButton>
          <PermissionButton permission="reports.export" variant="outlined" startIcon={<DownloadIcon />} onClick={exportXlsx}>XLSX</PermissionButton>
          <PermissionButton permission="reports.schedule" variant="contained" startIcon={<ScheduleIcon />} onClick={() => setSchedOpen(true)}>Schedule</PermissionButton>
        </Stack>
      </Stack>

      {/* Backend shape is { users:{dau,mau}, revenue:{today,last7d,last30d}, games:{active}, moderation:{pendingKyc,highRiskUsers} }.
          Older code looked for revenue.week / games.activeSessions / pending.kyc; keep both fallbacks so the page works on either. */}
      {(() => {
        const ov: any = overview.data || {};
        const toNum = (v: any): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
        const fmt = (v: any) => {
          if (v == null || Number.isNaN(Number(v))) return '—';
          const n = toNum(v);
          return n.toLocaleString();
        };
        const revWeek = ov.revenue?.week ?? ov.revenue?.last7d;
        const activeSessions = ov.games?.activeSessions ?? ov.games?.active;
        const pKyc = ov.pending?.kyc ?? ov.moderation?.pendingKyc ?? 0;
        const pPayouts = ov.pending?.payouts ?? ov.moderation?.pendingPayouts ?? 0;
        const pTickets = ov.pending?.tickets ?? ov.moderation?.openTickets ?? 0;
        return (
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography variant="caption" color="text.secondary">DAU / MAU</Typography>
                <Typography variant="h5" fontWeight={700}>{ov.users?.dau ?? '—'} / {ov.users?.mau ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">+{toNum(ov.users?.newToday)} today</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography variant="caption" color="text.secondary">Revenue today</Typography>
                <Typography variant="h5" fontWeight={700}>{fmt(ov.revenue?.today)}</Typography>
                <Typography variant="caption" color="text.secondary">week: {fmt(revWeek)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography variant="caption" color="text.secondary">Active sessions</Typography>
                <Typography variant="h5" fontWeight={700}>{activeSessions ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{toNum(ov.games?.today)} today</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography variant="caption" color="text.secondary">Pending work</Typography>
                <Stack direction="row" spacing={2}>
                  <Box><Typography variant="h6">{pKyc}</Typography><Typography variant="caption" color="text.secondary">KYC</Typography></Box>
                  <Box><Typography variant="h6">{pPayouts}</Typography><Typography variant="caption" color="text.secondary">Payouts</Typography></Box>
                  <Box><Typography variant="h6">{pTickets}</Typography><Typography variant="caption" color="text.secondary">Tickets</Typography></Box>
                </Stack>
              </CardContent></Card>
            </Grid>
          </Grid>
        );
      })()}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <FormControl size="small">
              <InputLabel>Range</InputLabel>
              <Select value={days} label="Range" onChange={(e) => setDays(Number(e.target.value))}>
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={180}>Last 180 days</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Revenue (daily)</Typography>
              <Box sx={{ height: 280 }}>
                {revenue.isLoading ? <Skeleton variant="rectangular" height={280} /> : (
                  <ResponsiveContainer>
                    <LineChart data={filteredRevenue}>
                      <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
                      <XAxis dataKey="date" stroke={ct.axisTick} />
                      <YAxis stroke={ct.axisTick} />
                      <RTooltip {...tt} />
                      <Line type="monotone" dataKey="revenue" stroke={CHART_SERIES[0]} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>User growth</Typography>
              <Box sx={{ height: 280 }}>
                {growth.isLoading ? <Skeleton variant="rectangular" height={280} /> : (
                  <ResponsiveContainer>
                    <AreaChart data={filteredGrowth}>
                      <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
                      <XAxis dataKey="date" stroke={ct.axisTick} />
                      <YAxis stroke={ct.axisTick} />
                      <RTooltip {...tt} />
                      <Area type="monotone" dataKey="activeUsers" stroke={CHART_SERIES[1]} fill={CHART_SERIES[1]} fillOpacity={0.3} />
                      <Area type="monotone" dataKey="newUsers" stroke={CHART_SERIES[5]} fill={CHART_SERIES[5]} fillOpacity={0.4} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Game performance</Typography>
              <Box sx={{ height: 280 }}>
                {perf.isLoading ? <Skeleton variant="rectangular" height={280} /> : (
                  <ResponsiveContainer>
                    <BarChart data={perf.data || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
                      <XAxis dataKey="gameType" stroke={ct.axisTick} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis stroke={ct.axisTick} />
                      <RTooltip {...tt} />
                      <Bar dataKey="plays" radius={[8, 8, 0, 0]}>
                        {((perf.data || []) as GamePerformance[]).map((_, i) => (
                          <Cell key={i} fill={seriesColor(i)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Retention cohorts</Typography>
              {cohorts.isLoading ? <Skeleton variant="rectangular" height={280} /> : cohortGrid.cohortList.length === 0 ? (
                <Alert severity="info">No cohort data yet.</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: 6, color: '#A3A3A3' }}>Cohort</th>
                        {cohortGrid.periods.map((p) => (
                          <th key={p} style={{ padding: 6, color: '#A3A3A3' }}>W{p}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cohortGrid.cohortList.map((c) => (
                        <tr key={c}>
                          <td style={{ padding: 6 }}>{c}</td>
                          {cohortGrid.periods.map((p) => {
                            const v = cohortGrid.map.get(`${c}:${p}`);
                            const intensity = v !== undefined ? Math.min(1, Math.max(0, v / 100)) : 0;
                            const bg = v === undefined ? '#1A1A1A' : `rgba(34, 197, 94, ${0.1 + intensity * 0.7})`;
                            return (
                              <td key={p} style={{ padding: 6, textAlign: 'center', background: bg, borderRadius: 4 }}>
                                {v !== undefined ? `${v.toFixed(0)}%` : '—'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={schedOpen} onClose={() => setSchedOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule report</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={schedName} onChange={(e) => setSchedName(e.target.value)} fullWidth />
            <TextField label="Recipient email" type="email" value={schedEmail} onChange={(e) => setSchedEmail(e.target.value)} fullWidth />
            <TextField label="Cron expression" value={schedCron} onChange={(e) => setSchedCron(e.target.value)} fullWidth helperText="e.g. 0 8 * * 1 = Mondays 08:00" />
            <FormControl fullWidth>
              <InputLabel>Kind</InputLabel>
              <Select value={schedKind} label="Kind" onChange={(e) => setSchedKind(e.target.value as typeof schedKind)}>
                <MenuItem value="revenue">Revenue</MenuItem>
                <MenuItem value="user_growth">User growth</MenuItem>
                <MenuItem value="game_performance">Game performance</MenuItem>
                <MenuItem value="retention">Retention</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchedOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!schedName.trim() || !schedEmail.trim() || !schedCron.trim() || schedule.isPending}
            onClick={() => schedule.mutate({ name: schedName, email: schedEmail, cron: schedCron, kind: schedKind })}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Analytics;
